import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { useYearRange } from '../constants/yearRange';
import { loadExpectedBills, loadPullByState, loadPullStates, workflowLabel } from '../utils/expectedBills';
import { tsRowsForParcel, staffNameFrom } from '../utils/assignments';
import useAssignments from '../hooks/useAssignments';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
import ColumnPicker from '../components/ColumnPicker';
import ScrollX, { pinCell, PINNED_TABLE, redrawKey } from '../components/ScrollX';
import { useAccountFallback, resolveAccount, accountTitle } from '../utils/accountFallback';

// Tax Bill Processing worklist (Bubble 1). TWO modes on one screen:
//  • DEFAULT (owed) — rows = expected_bills(year) the TS still owes (not_started/outstanding/rejected). Pristine.
//  • PULL — a CM/CS/TS clicks "Pull [state]"; data source swaps to pull_parcels_by_state (PARCEL-FIRST, date-
//    independent) so EVERY parcel in the state appears — including collector-less ones, flagged. Gaps sort to the
//    top and a loud banner counts them (a missed bill costs tens of thousands). Pull state persists in
//    sessionStorage (wh_pullState) so it survives the scanner round-trip (the scanner replaces this component).
// The logged-in TS's own rows sort to the top in default mode. Per-row 'Scan' launches the scanner; uncovered pull
// rows show 'Assign collector' (deep-link to the parcel) instead of a dead Scan.

const parseLocal = (s) => { if (!s) return null; const [y, m, d] = String(s).split('-').map(Number); return (y && m && d) ? new Date(y, m - 1, d) : null; };
const fmtDate = (s) => { const d = parseLocal(s); return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; };
const isGap = (r) => !!r.coverage_status && r.coverage_status !== 'covered';   // uncovered pull row (no collector / no schedule)

// Column config (render(row, ctx) — ctx supplies resolved TS + the status cell). Persisted/reordered via useColumnPrefs.
const WORKLIST_COLUMNS = [
  { key: 'state',           label: 'State',          visible: true, align: 'left',   render: (r) => r.state || '—' },
  { key: 'collector_name',  label: 'Collector Name', visible: true, align: 'left',   render: (r) => r.collector_name || '—' },
  { key: 'installment',     label: 'Installment #',  visible: true, align: 'center', render: (r) => (r.installment_number ?? '—') },
  { key: 'due_date',        label: 'Due Date',       visible: true, align: 'center', render: (r) => fmtDate(r.due_date) },
  { key: 'collector_number',label: 'Account Number', visible: true, align: 'left',   render: (r, ctx) => ctx.accountCell(r) },
  { key: 'client_name',     label: 'Client Name',    visible: true, align: 'left',   render: (r) => r.client_name || '—' },
  { key: 'property_name',   label: 'Property Name',  visible: true, align: 'left',   render: (r) => r.property_name || '—' },
  { key: 'ts',              label: 'TS',             visible: true, align: 'left',   render: (r, ctx) => ctx.tsName(r) || '—' },
  { key: 'status',          label: 'Status',         visible: true, align: 'left',   render: (r, ctx) => ctx.statusCell(r) },
];

const currentTaxYear = () => sessionStorage.getItem('wh_valueYear') || String(new Date().getFullYear());

export default function TaxBillWorklist({ darkMode, staffUser, onScanRow, onScanNew, onAssignCollector, onOpenParcel, refreshKey }) {
  // Year selector range ← folder_year_config (Admin → Year Management), one source for every
  // value-year picker in the app. Falls back to 2021-2028 if the config row can't be read.
  const { years } = useYearRange();
  const isDark = darkMode;
  const c = isDark
    ? { card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68', th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.03)', mine: 'rgba(180,0,0,0.10)', gap: 'rgba(180,0,0,0.12)', flag: '#fca5a5' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', input: '#ffffff', inputBorder: '#c8ccd8', th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)', mine: 'rgba(180,0,0,0.05)', gap: 'rgba(180,0,0,0.06)', flag: '#B40000' };
  const s = {
    input: { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '8px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '7px 12px', background: 'transparent', border: `1px solid ${c.inputBorder}`, borderRadius: '8px', color: c.sub, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnRed: { padding: '7px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    th: { padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
    td: { padding: '9px 14px', fontSize: '13px', color: c.text, borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}`, whiteSpace: 'nowrap' },
  };

  const [year, setYear] = useState(currentTaxYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  // ── Pull mode ──
  const [pulledState, setPulledState] = useState(() => sessionStorage.getItem('wh_pullState') || null);
  const [pullStates, setPullStates] = useState([]);
  const [pullSel, setPullSel] = useState('');            // dropdown selection before Pull is clicked
  const [hideCompleted, setHideCompleted] = useState(false);
  const { assignments, staff } = useAssignments();

  useEffect(() => { loadPullStates(year).then(setPullStates); }, [year]);   // dropdown source (states represented that year)

  // Persist the pulled state so it survives the scanner round-trip (the scanner replaces this component on scan).
  useEffect(() => {
    if (pulledState) sessionStorage.setItem('wh_pullState', pulledState);
    else sessionStorage.removeItem('wh_pullState');
  }, [pulledState]);

  // Data source swaps on pull: pull_parcels_by_state (all parcels, date-independent) vs expected_bills (owed).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const load = pulledState ? loadPullByState(pulledState, year) : loadExpectedBills(year);
    load.then(data => { if (alive) { setRows(data); setLoading(false); } });
    return () => { alive = false; };
    // refreshKey is bumped by StaffPortal when the parcel modal closes — an installment processed in the modal
    // must leave this list without a manual reload, or the row stays Outstanding and reads as a failed fix.
  }, [year, pulledState, refreshKey]);

  // ── Which rows already hold a BILLED amount, so the TS can process instead of scanning? ────────────────
  // ONE query for the whole list (paginated — parcel_taxes will pass 1000 rows), scanned client-side into a
  // Set of `parcelId|collectorId|installmentNumber`. The billed amount lives in parcel_taxes.installments[] as
  // {type:'advalorem', installment_number, amount} — written by the parcel modal's tax section, and by a scan
  // of a multi-stub bill, which is exactly why CA installment 2 is processable off installment 1's scan.
  // ⛔ NOT the computed amount: computed is a verification figure and is null for Cook prepay rows.
  const [billedKeys, setBilledKeys] = useState(() => new Set());
  useEffect(() => {
    let alive = true;
    (async () => {
      const found = new Set();
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase.from('parcel_taxes')
          .select('parcel_id, collector_id, installments').eq('tax_year', String(year)).range(from, from + 999);
        if (error) { console.warn('[worklist billed scan]', error.message); break; }   // degrade to Scan-only, don't blank the list
        (data || []).forEach(row => {
          (Array.isArray(row.installments) ? row.installments : []).forEach(line => {
            if (line && line.type === 'advalorem' && line.installment_number != null && line.amount != null) {
              found.add(`${row.parcel_id}|${row.collector_id}|${line.installment_number}`);
            }
          });
        });
        if (!data || data.length < 1000) break;
      }
      if (alive) setBilledKeys(found);
    })();
    return () => { alive = false; };
  }, [year, refreshKey]);
  const hasBilled = (r) => r.parcel_id && r.collector_id && r.installment_number != null
    && billedKeys.has(`${r.parcel_id}|${r.collector_id}|${r.installment_number}`);

  const { columns, save } = useColumnPrefs('tax_bill_worklist', WORKLIST_COLUMNS, staffUser?.id ? String(staffUser.id) : null);
  const visibleCols = (columns || WORKLIST_COLUMNS).filter(col => col.visible);
  // Coverage column is PULL-MODE ONLY and prepended — never persisted, so the default worklist + saved layouts stay clean.
  const coverageCol = {
    key: '_coverage', label: 'Coverage', align: 'left',
    render: (r) => isGap(r)
      ? <span style={{ fontWeight: '700', color: c.flag }}>{r.coverage_status === 'no_collector' ? 'No collector' : 'No schedule'}</span>
      : <span style={{ color: c.muted }}>Covered</span>,
  };
  // ⛔ CLIENT AND PROPERTY ARE PINNED ON THE LEFT, AND SO SHOWN FIRST (Philip 2026-10-27), at fixed widths with the
  // names wrapping; the other columns keep the column manager's order and scroll under them.
  const PIN_W = { client_name: 160, property_name: 220 };
  const unpinned = visibleCols.filter(col => !(col.key in PIN_W));
  const pinnedCols = Object.keys(PIN_W).map(k => visibleCols.find(col => col.key === k)).filter(Boolean);
  const renderCols = [...pinnedCols, ...(pulledState ? [coverageCol] : []), ...unpinned];
  // A pinned cell is opaque: the row's own tint laid over the card colour (the dark card is translucent).
  const solid = isDark ? '#2a3249' : '#ffffff';
  const pinAt = (col, tint, header = false) => {
    const i = pinnedCols.indexOf(col);
    if (i < 0) return {};
    const w = PIN_W[col.key];
    const left = pinnedCols.slice(0, i).reduce((sum, p) => sum + PIN_W[p.key], 0);
    return { width: `${w}px`, minWidth: `${w}px`, maxWidth: `${w}px`, whiteSpace: 'normal', wordBreak: 'break-word',
      ...pinCell(left, { header, last: i === pinnedCols.length - 1, background: tint && tint !== 'transparent' ? `linear-gradient(${tint}, ${tint}), ${solid}` : solid }) };
  };

  // Resolve the parcel's TS (ts_id override → else the client's state-aware TS assignment) → staff id + name.
  const tsFor = useMemo(() => (r) => {
    const row = tsRowsForParcel(assignments, r.client_id, { state: r.state, ts_id: r.ts_id })[0];
    const id = row?.staff_id || null;
    return { id, name: id ? staffNameFrom(staff, id) : '', mine: !!id && String(id) === String(staffUser?.id) };
  }, [assignments, staff, staffUser]);

  // Account Number: collector link → that year's parcel record → the parcel (utils/accountFallback; display only).
  const acctFb = useAccountFallback(rows, year);
  const acctOf = (r) => resolveAccount(r, acctFb);

  const ctx = {
    tsName: (r) => tsFor(r).name,
    accountCell: (r) => { const a = acctOf(r); return <span title={accountTitle(a, year)}>{a.value || '—'}</span>; },
    // Uncovered pull rows show the LOUD coverage flag (not a bill status). A 'rejected' bill = CS returned it → amber
    // "returned — CS issue" flag + rejection_note. Orphans never reach this list (they carry a bill → Approvals).
    statusCell: (r) => {
      if (isGap(r)) return <span style={{ fontWeight: '700', color: c.flag }}>{r.coverage_status === 'no_collector' ? 'No collector assigned' : 'Collector has no installment schedule'}</span>;
      return r.workflow_status === 'rejected'
        ? (
          <span title={r.rejection_note || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontWeight: '700', color: isDark ? '#fbbf24' : '#8A5B00' }}>⚠ Returned — CS issue</span>
            {r.rejection_note && <span style={{ display: 'inline-block', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom', fontSize: '11px', color: c.muted }}>{r.rejection_note}</span>}
          </span>
        )
        : <span style={{ fontWeight: '600' }}>{workflowLabel(r)}</span>;
    },
  };

  // List assembly. Default (owed) mode: not_started/outstanding/rejected. Pull mode: ALL rows (date-independent),
  // optionally hiding completed (filed/transmitted) — pull NEVER applies the owed status-filter.
  const baseList = useMemo(() => {
    if (pulledState) return hideCompleted ? rows.filter(r => !['filed', 'transmitted'].includes(r.workflow_status)) : rows;
    // ⛔ ALLOW-LIST — 'overdue' must be here. expected_bills buckets due_date < today as 'overdue' (ahead of
    // the 45-day rung); omitting it would drop the most urgent rows off this screen silently. No sort change
    // is needed: the ordering below is by due_date, so overdue rows rise to the top on their own.
    return rows.filter(r => ['overdue', 'not_started', 'outstanding', 'rejected'].includes(r.workflow_status));
  }, [rows, pulledState, hideCompleted]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = baseList;
    if (q) list = list.filter(r => [r.state, r.collector_name, acctOf(r).value, r.client_name, r.property_name, tsFor(r).name].some(v => (v || '').toLowerCase().includes(q)));
    return [...list].sort((a, b) => {
      if (pulledState) {
        // Pull: coverage gaps to the TOP (above outstanding), then earliest due date (nulls last).
        const ag = isGap(a) ? 0 : 1, bg = isGap(b) ? 0 : 1;
        if (ag !== bg) return ag - bg;
        return (parseLocal(a.due_date)?.getTime() || Infinity) - (parseLocal(b.due_date)?.getTime() || Infinity);
      }
      // Default: mine first, then earliest due date.
      const am = tsFor(a).mine ? 0 : 1, bm = tsFor(b).mine ? 0 : 1;
      if (am !== bm) return am - bm;
      return (parseLocal(a.due_date)?.getTime() || Infinity) - (parseLocal(b.due_date)?.getTime() || Infinity);
    });
  }, [baseList, search, tsFor, pulledState, acctFb]);

  // Coverage banner (pull mode) — an unmissable aggregate. Per PARCEL, keep the WORST coverage seen
  // (no_collector > collector_no_installments > covered), so partial-gap parcels are still counted.
  const coverage = useMemo(() => {
    if (!pulledState) return null;
    const worst = new Map();
    for (const r of rows) {
      const rank = r.coverage_status === 'no_collector' ? 2 : r.coverage_status === 'collector_no_installments' ? 1 : 0;
      const cur = worst.get(r.parcel_id);
      if (cur == null || rank > cur) worst.set(r.parcel_id, rank);
    }
    let noCollector = 0, noSchedule = 0;
    for (const rank of worst.values()) { if (rank === 2) noCollector++; else if (rank === 1) noSchedule++; }
    return { total: worst.size, noCollector, noSchedule };
  }, [rows, pulledState]);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text }}>Tax Bill Processing</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', flexWrap: 'wrap' }}>
          {!pulledState ? (
            <>
              <select value={pullSel} onChange={e => setPullSel(e.target.value)} style={{ ...s.input, width: 'auto' }} title="Pull every parcel in a state into processing (date-independent)">
                <option value="">Pull state…</option>
                {pullStates.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
              <button style={{ ...s.btnRed, opacity: pullSel ? 1 : 0.5, cursor: pullSel ? 'pointer' : 'default' }} disabled={!pullSel} onClick={() => pullSel && setPulledState(pullSel)}>⬇ Pull parcels</button>
            </>
          ) : (
            <>
              <span style={{ fontSize: '12px', fontWeight: '700', color: c.text, background: c.gap, border: '1px solid var(--red)', borderRadius: '8px', padding: '6px 10px' }}>Pulled: {pulledState}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: c.sub }}><input type="checkbox" checked={hideCompleted} onChange={e => setHideCompleted(e.target.checked)} /> Hide completed</label>
              <button style={s.btnGhost} onClick={() => { setPulledState(null); setPullSel(''); setHideCompleted(false); }}>✕ Clear pull</button>
            </>
          )}
          <select value={year} onChange={e => setYear(e.target.value)} style={{ ...s.input, width: 'auto' }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={s.btnRed} onClick={() => onScanNew && onScanNew()}>Scan a bill</button>
          <button style={s.btnGhost} onClick={() => setPickerOpen(o => !o)}>⚙ Columns</button>
          {pickerOpen && <ColumnPicker columns={columns} onSave={async (n) => { await save(n); setPickerOpen(false); }} onClose={() => setPickerOpen(false)} c={c} s={s} isDark={isDark} />}
        </div>
      </div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>
        {pulledState
          ? `All ${pulledState} parcels pulled for ${year} (date-independent) — coverage gaps are pinned to the top.`
          : `Bills that still need processing for ${year} — your bills are pinned to the top.`}
      </div>

      {/* Coverage banner — LOUD, can't be scrolled past. Gaps = parcels that will NOT bill until fixed. */}
      {pulledState && coverage && (
        <div style={{ marginBottom: '14px', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${(coverage.noCollector || coverage.noSchedule) ? 'var(--red)' : (isDark ? '#2f6b46' : '#b7e0c4')}` }}>
          {coverage.noCollector > 0 && (
            <div style={{ padding: '12px 16px', background: c.gap, color: c.flag, fontWeight: '700', fontSize: '14px' }}>
              {coverage.noCollector} of {coverage.total} {pulledState} parcels have NO collector assigned — these will NOT generate bills until fixed.
            </div>
          )}
          {coverage.noSchedule > 0 && (
            <div style={{ padding: '10px 16px', background: isDark ? 'rgba(251,191,36,0.10)' : 'rgba(251,191,36,0.12)', color: isDark ? '#fbbf24' : '#8A5B00', fontWeight: '600', fontSize: '13px' }}>
              {coverage.noSchedule} parcel(s) have a collector with NO installment schedule imported for {year} — also won't bill until the collector's schedule is loaded.
            </div>
          )}
          {coverage.noCollector === 0 && coverage.noSchedule === 0 && (
            <div style={{ padding: '12px 16px', background: isDark ? 'rgba(47,107,70,0.15)' : 'rgba(183,224,196,0.35)', color: isDark ? '#86efac' : '#15803d', fontWeight: '700', fontSize: '14px' }}>
              All {coverage.total} {pulledState} parcels have collectors assigned.
            </div>
          )}
        </div>
      )}

      <div style={{ maxWidth: '360px', marginBottom: '14px' }}>
        <input style={s.input} placeholder="Search collector, client, property, Account Number…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <ScrollX>
          <table style={{ width: '100%', ...PINNED_TABLE }}>
            <thead><tr>
              {renderCols.map(col => <th key={col.key} style={{ ...s.th, textAlign: col.align === 'center' ? 'center' : 'left', ...pinAt(col, c.th, true) }}>{col.label}</th>)}
              <th style={{ ...s.th, textAlign: 'right' }}>Action</th>
            </tr></thead>
            <tbody key={redrawKey(filtered.map((r, i) => `${r.parcel_id}|${r.collector_id ?? '-'}|${r.installment_number ?? '-'}|${i}`))}>
              {loading
                ? <tr><td colSpan={renderCols.length + 1} style={{ ...s.td, textAlign: 'center', color: c.muted }}>Loading…</td></tr>
                : filtered.length === 0
                  ? <tr><td colSpan={renderCols.length + 1} style={{ ...s.td, textAlign: 'center', color: c.muted }}>{search ? 'No matching rows.' : (pulledState ? `No ${pulledState} parcels.` : 'Nothing to process.')}</td></tr>
                  : filtered.map((r, i) => {
                    const gap = isGap(r);
                    const mine = !pulledState && tsFor(r).mine;
                    const bg = gap ? c.gap : (mine ? c.mine : 'transparent');
                    return (
                      <tr key={`${r.parcel_id}|${r.collector_id ?? '-'}|${r.installment_number ?? '-'}|${i}`}
                        style={{ background: bg, cursor: onOpenParcel && r.parcel_id ? 'pointer' : 'default' }}
                        title={onOpenParcel && r.parcel_id ? 'Open this parcel on the Taxes section — process from data already entered, no scan needed' : undefined}
                        onClick={() => onOpenParcel && r.parcel_id && onOpenParcel(r, year)}
                        onMouseEnter={e => { if (bg === 'transparent') e.currentTarget.style.background = c.rowHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = bg; }}>
                        {renderCols.map(col => <td key={col.key} style={{ ...s.td, textAlign: col.align === 'center' ? 'center' : 'left', ...pinAt(col, bg) }}>{col.render(r, ctx)}</td>)}
                        <td style={{ ...s.td, textAlign: 'right' }}>
                          {gap
                            ? <button style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px', borderColor: 'var(--red)', color: c.flag, fontWeight: '700' }} title="Assign this parcel's collector(s) so it can generate bills" onClick={(e) => { e.stopPropagation(); onAssignCollector && onAssignCollector(r.parcel_id, year); }}>⚠ Assign collector</button>
                            : hasBilled(r)
                              // A billed amount is already entered for THIS installment (commonly written by a
                              // multi-stub scan of installment 1, which is why CA installment 2 lands here).
                              // Opening the parcel on that installment's card is the process route — there is no
                              // second submit path; processBill lives in the modal and only there.
                              ? <button style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px', borderColor: 'var(--red)', color: 'var(--red)', fontWeight: '700' }} title="Amount already entered — open this installment's card to submit it to Approval, no scan needed" onClick={(e) => { e.stopPropagation(); onOpenParcel && onOpenParcel(r, year); }}>→ Process</button>
                              // Nothing entered for this installment: it genuinely needs the document. Scan, not a
                              // disabled Process — a greyed button here just prompts "why can't I?".
                              : <button style={{ ...s.btnGhost, padding: '4px 10px', fontSize: '11px' }} title="Scan a bill pre-targeted to this row" onClick={(e) => { e.stopPropagation(); onScanRow && onScanRow(r, year); }}>🔍 Scan</button>}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </ScrollX>
      </div>
    </div>
  );
}
